import { createParser, parseAsString, useQueryState } from 'nuqs';
import { useCallback, useMemo } from 'react';

import {
  filterOperatorSchemas,
  filterValueSchemas,
  type FilterDefs,
  type FilterState,
  type Filter,
  SUPPORTED_OPERATORS,
  type ActiveFilter,
  type UnsafeFilter,
  unsafeFilterSchema,
  FilterId,
} from './types';

function isValidFilter<TFilterDefs extends FilterDefs, TId extends FilterId<TFilterDefs>>(
  item: UnsafeFilter,
  filterDefs: TFilterDefs,
): item is Filter<TFilterDefs, TId> {
  const filterDef = filterDefs[item.id];

  if (!filterDef) {
    return false;
  }

  return (
    filterOperatorSchemas[filterDef.variant].safeParse(item.operator).success &&
    filterValueSchemas[filterDef.variant].safeParse(item.value).success
  );
}

function sanitizeFilterState<TFilterDefs extends FilterDefs>(
  rawFilterState: unknown[],
  filterDefs: TFilterDefs,
): FilterState<TFilterDefs> {
  const filterState: FilterState<TFilterDefs> = [];

  rawFilterState.forEach((rawFilter) => {
    const parsedRawFilter = unsafeFilterSchema.safeParse(rawFilter);

    if (!parsedRawFilter.success) {
      return;
    }

    if (isValidFilter(parsedRawFilter.data, filterDefs)) {
      filterState.push(parsedRawFilter.data);
    }
  });

  return filterState;
}

export interface FiltersInstance<TFilterDefs extends FilterDefs> {
  readonly filterDefs: TFilterDefs;
  readonly filterState: FilterState<TFilterDefs>;
  readonly search: string;
  addFilter: (filterId: string) => Promise<void>;
  clearAll: () => Promise<void>;
  getActiveFilterMap: () => Partial<{
    [K in FilterId<TFilterDefs>]: ActiveFilter<TFilterDefs, K>;
  }>;
  removeFilter: (filterId: string) => Promise<void>;
  setFilterState: (unsafeFilterState: UnsafeFilter[]) => Promise<void>;
  updateFilter: (filter: UnsafeFilter) => Promise<void>;
  updateSearch: (search: string) => Promise<void>;
}

export function useFilters<TFilterDefs extends FilterDefs>(config: {
  filterDefs: TFilterDefs;
  initialState?: FilterState<TFilterDefs>;
  /**
   * optional callback that fires when filters change as result
   * of *user* interactions.
   * - Does not fire on setFilterState
   * - Does not fire on addFilter since new filters have an undefined value
   *   and would not cause a change in filtered data
   */
  onFilterInteraction?: () => void | Promise<any>;
}): FiltersInstance<TFilterDefs> {
  const { filterDefs, initialState, onFilterInteraction } = config;
  const sanitizedInitialState = useMemo(
    () => sanitizeFilterState(initialState ?? [], filterDefs),
    [initialState, filterDefs],
  );

  const urlParser = createParser({
    parse: (value: string): FilterState<TFilterDefs> => {
      try {
        const rawFilters = JSON.parse(value);
        return sanitizeFilterState(rawFilters, filterDefs);
      } catch {
        console.warn('error parsing filters as JSON');
        return [];
      }
    },
    serialize: (value: FilterState<TFilterDefs>) => JSON.stringify(value),
    eq: (a, b) => a === b || JSON.stringify(a) === JSON.stringify(b),
  }).withDefault(sanitizedInitialState);

  const [filterState, setFilterStateInternal] = useQueryState('filters', urlParser);
  const [search, setSearch] = useQueryState('search', parseAsString.withDefault(''));

  const addFilter = useCallback(
    async (filterId: string) => {
      const filterDef = filterDefs[filterId];

      if (!filterDef) {
        console.warn(`addFilter failed: filter definition not found for ${filterId}`);
        return;
      }

      await setFilterStateInternal((prev) => {
        if (prev.some((filter) => filter.id === filterId)) {
          console.warn(`addFilter failed: filter already exists for ${filterId}`);
          return prev;
        }

        const newFilter = {
          id: filterId,
          operator: SUPPORTED_OPERATORS[filterDef.variant][0],
          value: undefined,
        };

        if (!isValidFilter(newFilter, filterDefs)) {
          console.warn(`addFilter failed: invalid filter for ${filterId}`);
          return prev;
        }

        return [...prev, newFilter];
      });
    },
    [filterDefs, setFilterStateInternal],
  );

  const clearAll = useCallback(async () => {
    await Promise.all([setSearch(''), setFilterStateInternal([]), onFilterInteraction?.()]);
  }, [setSearch, setFilterStateInternal, onFilterInteraction]);

  const getActiveFilterMap = useMemo(() => {
    let cachedValue:
      | Partial<{
          [K in FilterId<TFilterDefs>]: ActiveFilter<TFilterDefs, K>;
        }>
      | undefined;
    return () => {
      if (!cachedValue) {
        cachedValue = filterState.reduce(
          (activeFilterMap, filter) => {
            if (filter.value !== undefined && filter.value !== null) {
              return {
                ...activeFilterMap,
                [filter.id]: {
                  id: filter.id,
                  operator: filter.operator,
                  value: filter.value,
                },
              };
            }

            return activeFilterMap;
          },
          {} as Partial<{
            [K in FilterId<TFilterDefs>]: ActiveFilter<TFilterDefs, K>;
          }>,
        );
      }
      return cachedValue;
    };
  }, [filterState]);

  const removeFilter = useCallback(
    async (filterId: string) => {
      await setFilterStateInternal((prev) =>
        prev.filter((filter) => {
          if (filter.id === filterId) {
            if (filter.value !== undefined) void onFilterInteraction?.();
            return false;
          }
          return true;
        }),
      );
    },
    [setFilterStateInternal, onFilterInteraction],
  );

  const setFilterState = useCallback(
    async (nextFilterState: UnsafeFilter[]) => {
      await setFilterStateInternal(
        nextFilterState.filter((filter) => isValidFilter(filter, filterDefs)),
      );
    },
    [filterDefs, setFilterStateInternal],
  );

  const updateFilter = useCallback(
    async (unsafeFilter: UnsafeFilter) => {
      await Promise.all([
        setFilterStateInternal((prev) =>
          prev.map((filter) => {
            if (filter.id !== unsafeFilter.id) {
              return filter;
            }

            const filterDef = filterDefs[unsafeFilter.id];
            if (!filterDef) {
              console.warn(
                `updateFilter failed: filter definition not found for ${unsafeFilter.id}`,
              );
              return filter;
            }

            const updatedFilter = {
              ...filter,
              operator: unsafeFilter.operator,
              value: unsafeFilter.value,
            };

            if (!isValidFilter(updatedFilter, filterDefs)) {
              console.warn(`updateFilter failed: invalid filter for ${unsafeFilter.id}`);
              return filter;
            }

            return updatedFilter;
          }),
        ),
        onFilterInteraction?.(),
      ]);
    },
    [filterDefs, setFilterStateInternal, onFilterInteraction],
  );

  const updateSearch = useCallback(
    async (nextSearch: string) => {
      await Promise.all([setSearch(nextSearch), onFilterInteraction?.()]);
    },
    [setSearch, onFilterInteraction],
  );

  return useMemo(
    () => ({
      filterDefs,
      filterState,
      search,
      addFilter,
      clearAll,
      getActiveFilterMap,
      removeFilter,
      setFilterState,
      updateFilter,
      updateSearch,
    }),
    [
      filterDefs,
      filterState,
      search,
      addFilter,
      clearAll,
      getActiveFilterMap,
      removeFilter,
      setFilterState,
      updateFilter,
      updateSearch,
    ],
  );
}
